/**
 * Konstanten für die sechs Dimensionen plus die vorgelagerte Triage.
 *
 * `id` = interner Code-Schlüssel (auch URL-Slug für /case/:id/wizard/:step)
 * `nummer` = zweistellige Anzeige-Nummer im Editorial-Stil (Magazin-Look)
 * `name` = externer Name (Mandanten-/Jobcenter-Sicht). KEIN V-Vokabular.
 * `subtitle` = italic-Untertitel für Modul-Cards
 * `accentVar` = CSS-Variable für die Pastell-Akzentfarbe (40–60% Opacity)
 * `accentTint` = optisches Tailwind-Pastell-Fallback, falls die CSS-Variable
 *                noch nicht gesetzt ist (entspricht den Brand-Token-Werten).
 * `weight` = Gewichtung im Gesamtscore (Konzept §5.2 – nur informativ in der UI)
 *
 * Reihenfolge ist verbindlich: erst Triage, dann sechs Dimensionen.
 */

import { de } from '../../lib/i18n/de';

export type DimensionKey = 'identity' | 'activity' | 'style' | 'resources' | 'health' | 'private';

export type StepKey = 'triage' | DimensionKey;

export interface StepDescriptor {
  /** URL-Slug, gleichzeitig interner Code-Schlüssel */
  readonly id: StepKey;
  /** Anzeige-Nummer (zweistellig, mit führender Null) – „00" für Triage */
  readonly nummer: string;
  /** Externer Name (Mandanten-Sicht, keine V-Terminologie) */
  readonly name: string;
  /** Italic-Untertitel auf der Modul-Card */
  readonly subtitle: string;
  /** CSS-Variable für Pastell-Akzentblock (40–60% Opacity) */
  readonly accentVar: string;
  /** Hex-Wert als Fallback (wenn CSS-Var nicht aufgelöst) */
  readonly accentHex: string;
  /** Score-Gewichtung in Prozent (informativ, Quelle Konzept §5.2) */
  readonly weight: number | null;
  /** Triage ist Pflicht-Eintrittspunkt; Dimensionen sind frei wählbar nach Triage */
  readonly required: boolean;
  /** AP6: Modul ist als Stub angelegt; Resources kommt vollständig in AP6b */
  readonly comingSoon: boolean;
}

/*
 * Modul-Renumbering (Phase B):
 *   - Modul 00 = Stammdaten (gerendert separat in MandantCaseHome / CaseHub —
 *     nicht Teil dieser STEPS-Liste, weil es eine andere Datenmodell-Quelle
 *     hat als die Wizard-Dimension-Slices)
 *   - Modul 01 = Triage / Erstkontakt
 *   - Modul 02 = Identität & Qualifikation
 *   - Modul 03 = Aktivität & Geschäftsmodell
 *   - Modul 04 = Stil & Marktposition
 *   - Modul 05 = Ressourcen & Finanzen
 *   - Modul 06 = Gesundheit & Resilienz
 *   - Modul 07 = Privates Umfeld & Bedarfsgemeinschaft
 *
 * URL-Slugs (`triage`, `identity`, …) bleiben unverändert — sonst brechen
 * Magic-Links und Bookmarks. Renumbering ist NUR ein Display-String.
 */
export const STEPS: readonly StepDescriptor[] = [
  {
    id: 'triage',
    nummer: '01',
    name: de.triage.name,
    subtitle: de.triage.subtitle,
    accentVar: 'var(--color-stone-300)',
    accentHex: '#d6d3d1',
    weight: null,
    required: true,
    comingSoon: false,
  },
  {
    id: 'identity',
    nummer: '02',
    name: de.dimensions.identity.name,
    subtitle: de.dimensions.identity.subtitle,
    accentVar: 'var(--color-dim-identity)',
    accentHex: '#f6c731',
    weight: 15,
    required: false,
    comingSoon: false,
  },
  {
    id: 'activity',
    nummer: '03',
    name: de.dimensions.activity.name,
    subtitle: de.dimensions.activity.subtitle,
    accentVar: 'var(--color-dim-activity)',
    accentHex: '#b8a07a',
    weight: 20,
    required: false,
    comingSoon: false,
  },
  {
    id: 'style',
    nummer: '04',
    name: de.dimensions.style.name,
    subtitle: de.dimensions.style.subtitle,
    accentVar: 'var(--color-dim-style)',
    accentHex: '#f5b4c4',
    weight: 15,
    required: false,
    comingSoon: false,
  },
  {
    id: 'resources',
    nummer: '05',
    name: de.dimensions.resources.name,
    subtitle: de.dimensions.resources.subtitle,
    accentVar: 'var(--color-dim-resources)',
    accentHex: '#4f5da6',
    weight: 30,
    required: false,
    comingSoon: false,
  },
  {
    id: 'health',
    nummer: '06',
    name: de.dimensions.health.name,
    subtitle: de.dimensions.health.subtitle,
    accentVar: 'var(--color-dim-health)',
    accentHex: '#e8705a',
    weight: 10,
    required: false,
    comingSoon: false,
  },
  {
    id: 'private',
    nummer: '07',
    name: de.dimensions.private.name,
    subtitle: de.dimensions.private.subtitle,
    accentVar: 'var(--color-dim-private)',
    accentHex: '#3aaa6d',
    weight: 10,
    required: false,
    comingSoon: false,
  },
] as const;

/** Schnell-Lookup nach StepKey (case-/typo-sicher über DimensionKey). */
export function getStep(id: StepKey): StepDescriptor {
  const step = STEPS.find((s) => s.id === id);
  if (!step) {
    throw new Error(`Unknown step id: ${id}`);
  }
  return step;
}

/** Reine Dimensionen ohne Triage (für Auswertung/Score). */
export const DIMENSION_KEYS = STEPS.filter((s) => s.id !== 'triage').map(
  (s) => s.id as DimensionKey,
);

/** Total-Anzahl Steps für Progress-Indikator. */
export const TOTAL_STEPS = STEPS.length;
